/**
 * Unified error display for execute resolve / network failures.
 * White theme aligned with the rest of the task-execution flow.
 */
import X9Logo from './X9Logo';

interface ErrorScreenProps {
  reason: string;
  onRetry?: () => void;
  /** Sign out the current phone session and collect a new number. */
  onChangePhone?: () => void;
  /** Default task copy; run links use the same reasons with this noun. */
  noun?: 'task' | 'run';
}

interface Copy {
  title: string;
  body: string;
  retryLabel?: string;
  changePhoneLabel?: string;
}

const COPY: Record<string, Copy> = {
  not_found: {
    title: 'Task not found',
    body: 'This link may have expired or been revoked by the task creator. Ask them to share a new one.',
  },
  expired: {
    title: 'Link expired',
    body: 'This task link has expired. Ask the creator to share a new one.',
  },
  phone_mismatch: {
    title: 'Wrong phone number',
    body: 'This browser is signed in with a different number than the one this task was sent to. Verify with the number they assigned, or ask the owner to reassign.',
    retryLabel: 'Try again',
    changePhoneLabel: 'Use a different number',
  },
  auth_required: {
    title: 'Verification needed',
    body: 'We need to verify your phone number before you can view or work on this task.',
    retryLabel: 'Verify phone',
  },
  not_assignee: {
    title: 'Not your task',
    body: 'This task is assigned to someone else. Only the assignee can open and work on it with their verified phone number.',
  },
  network_error: {
    title: 'Connection error',
    body: "We couldn't reach our servers. Check your connection and try again.",
    retryLabel: 'Retry',
  },
  timeout: {
    title: 'Taking too long',
    body: "Your connection was too slow to finish opening this. Move somewhere with better signal, then retry.",
    retryLabel: 'Retry',
  },
  app_check_failed: {
    title: 'Verification failed',
    body: "We couldn't verify your browser. Reload the page and try again.",
    retryLabel: 'Reload',
  },
  revoked: {
    title: 'Link revoked',
    body: 'This link is no longer valid. Ask the owner to share a new one.',
  },
  settled: {
    title: 'This run is finished',
    body: 'Every step has already been resolved. Ask the owner if you need a new run.',
  },
};

const RUN_COPY: Record<string, Copy> = {
  not_found: {
    title: 'Run not found',
    body: 'This link may have expired or been revoked. Ask the owner to share a new one.',
  },
  expired: {
    title: 'Link expired',
    body: 'This run link has expired. Ask the owner to share a new one.',
  },
  phone_mismatch: {
    title: 'Wrong phone number',
    body: 'This browser is signed in with a different number than the one this run was sent to. Verify with the number they dispatched to, or ask the owner to reassign.',
    retryLabel: 'Try again',
    changePhoneLabel: 'Use a different number',
  },
  auth_required: {
    title: 'Verification needed',
    body: 'We need to verify your phone number before you can open this run.',
    retryLabel: 'Verify phone',
  },
};

const DEFAULT_COPY: Copy = {
  title: 'Something went wrong',
  body: 'An unexpected error occurred. Reload the page and try again.',
  retryLabel: 'Reload',
};

export default function ErrorScreen({
  reason,
  onRetry,
  onChangePhone,
  noun = 'task',
}: ErrorScreenProps) {
  const copy = (noun === 'run' && RUN_COPY[reason]) || COPY[reason] || DEFAULT_COPY;
  const retryLabel = copy.retryLabel;
  const changePhoneLabel = onChangePhone ? copy.changePhoneLabel : undefined;
  const retryHandler = onRetry || (() => window.location.reload());
  const showFooter = Boolean(retryLabel || changePhoneLabel);

  return (
    <div className="execute-screen">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-10 pt-12 text-center">
        <div className="mx-auto flex w-[min(17vw,4.2rem)] flex-col items-center text-x9-marine">
          <X9Logo title="X9" className="block w-full select-none" />
        </div>

        <h1 className="mt-7 text-xl font-semibold tracking-tight text-x9-ink">{copy.title}</h1>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-x9-marineSoft">
          {copy.body}
        </p>
      </div>

      {showFooter && (
        <footer className="page-footer">
          <div className="mx-auto flex w-full max-w-md flex-col gap-3">
            {retryLabel && (
              <button
                type="button"
                onClick={retryHandler}
                className="btn-paper-primary w-full"
              >
                {retryLabel}
              </button>
            )}
            {changePhoneLabel && (
              <button
                type="button"
                onClick={onChangePhone}
                className="btn-paper-secondary w-full"
              >
                {changePhoneLabel}
              </button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}
